import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Wizard } from 'components/wizard';
import { Breadcrumbs } from 'components/navigation';
import { Intent } from '@blueprintjs/core';
import { Button } from 'components/buttons';
import { HelperFunctions } from 'helpers';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckService } from 'services/CheckService';
import { AssetService, LocationService, UserService } from 'services';
import { AlertUnsavedData, NotificationToaster } from 'components/notifications';
import { TemplateDetails } from 'pages/checks/templates/shared/template-details';
import { TemplateSettings } from 'pages/checks/templates/shared/template-settings';
import { TemplateRolePermissions } from 'pages/checks/templates/shared/template-role-permissions';
import { useDirtyData } from 'hooks/useDirtyData';
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess } from 'components/user-access';
import { useTemplateConfig } from 'pages/checks/hooks/useTemplateConfig';

export function NewTemplate(props) {
    const defaultTemplate = {
        name: "",
        status: "Draft",
        subCategory: "",
        rolesWhoCanCompleteChecks: [],
        conditions: {},
        signatureRequiredWhenCompletingCheck: false,
        allowManagerSignOff: false,
        minimumTimeRequiredToFillOutCheckInMinutes: 0,
        timeThresholdInMinutes: 0,
    }

    const { useGlobalEndpoint, requiredAction, baseRoute, isGlobal } = useTemplateConfig(props.useGlobalConfig);
    const [template, setTemplate] = useState({ ...defaultTemplate });
    const [originalTemplate, setOriginalTemplate] = useState({ ...defaultTemplate });
    const [category, setCategory] = useState("");

    const [userRoles, setUserRoles] = useState([]);
    const [assetOptions, setAssetOptions] = useState({});
    const [locationBusinessAreas, setLocationBusinessAreas] = useState([]);

    const [templateOptions, setTemplateOptions] = useState({
        templateCategories: []
    });

    const [createdTemplateId, setCreatedTemplateId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [wizardStep, setWizardStep] = useState(0);
    const [wizardValid, setWizardValid] = useState([false, false, false]);

    const isTemplateDirty = useDirtyData(originalTemplate, template);

    var navigate = useNavigate();

    useEffect(() => {
        axios.all([
            CheckService.getTemplateOptions(),
            AssetService.getAssetOptions(useGlobalEndpoint),
            LocationService.getLocationBusinessAreas(),
            UserService.getRoles()
        ]).then(axios.spread((templateOptionsResponse, assetOptionsResponse, locationBusinessAreasResponse, userRolesResponse) => {
            setUserRoles(userRolesResponse);
           
            let filteredAssetOptions = assetOptionsResponse;

            if (useGlobalEndpoint && filteredAssetOptions?.assetTypes) {
                filteredAssetOptions = {
                    ...filteredAssetOptions,
                    assetTypes: filteredAssetOptions.assetTypes.map(type => ({
                        ...type,
                        subtypes: []
                    }))
                };
            }

            setAssetOptions(filteredAssetOptions);
            setLocationBusinessAreas(locationBusinessAreasResponse);

            setTemplateOptions(templateOptionsResponse);
            setOriginalTemplate({ ...defaultTemplate });
            setLoading(false);
        }),
            () => {
                NotificationToaster.show(Intent.DANGER, "Could not load template. Please try again.");
            })
    }, []);

    useEffect(() => {
        if (template.subCategory && templateOptions) {
            setCategory(calculateCategoryFromSubCategory());
        }
    }, [template, templateOptions])

    const calculateCategoryFromSubCategory = () => {
        return templateOptions.templateCategories
            .find(c => c.subCategories.some(s => s.key === template.subCategory))
            ?.key;
    }

    const onWizardChange = (step, direction, previousStep) => {
        setWizardStep(step);
    }

    const onWizardStepValidationChange = (stepIndex, isValid) => {
        const validationClone = HelperFunctions.deepClone(wizardValid);
        validationClone[stepIndex] = isValid;
        setWizardValid(validationClone);
    }

    const getAdditionalButtons = () => {
        return (
            <Button
                intent="secondary"
                text="Cancel"
                onClick={() => navigate(baseRoute)}
                disabled={saving}
                id="cancel-btn"
            />
        );
    }

    const addTemplate = () => {
        setSaving(true);

        if (wizardValid.some(x => !x)) {
            NotificationToaster.show(Intent.WARNING, "There are errors on the page that need your attention");
            setSaving(false);
            return;
        }

        const templateToSave = {
            $subCategory: template.subCategory,
            ...template
        }

        CheckService.createTemplate(templateToSave, useGlobalEndpoint).then(
            (newId) => {
                setSaving(false);
                setCreatedTemplateId(newId);
                NotificationToaster.show(Intent.SUCCESS, "Template created successfully.");
                // Defer so React commits the setCreatedTemplateId render and
                // AlertUnsavedData clears its blocker ref before navigation fires.
                setTimeout(() => navigate(`${baseRoute}/${newId}`), 0);
            },
            (err) => {
                NotificationToaster.show(Intent.DANGER, err);
                setSaving(false);
            }
        );
    }

    return <UserAccess perform={[requiredAction]}
        yes={() => (<div className="row">
            <Breadcrumbs items={props.breadcrumbs} />
            <h1>{isGlobal ? "New Standard Template" : "New Template"}</h1>

            <Wizard
                id="new-template-wizard"
                onStepChange={onWizardChange}
                onFinish={addTemplate}
                canProceed={wizardValid[wizardStep]}
                disabled={saving}
                alignButtonsRight={true}
                additionalButtonsStart={getAdditionalButtons()}
                finishButtonText="Create Template"
                footer={() => null}
                allowBack={true}
                canFinish={true}>
                <TemplateDetails
                    template={template}
                    onChange={setTemplate}
                    loading={loading}
                    saving={saving}
                    onValidationChange={(valid) => onWizardStepValidationChange(0, valid)}
                    isReadOnly={false}
                    isNew={true}
                    templateOptions={templateOptions}
                    assetOptions={assetOptions}
                    locationBusinessAreas={locationBusinessAreas}
                />
                <TemplateSettings
                    template={template}
                    onChange={setTemplate}
                    loading={loading}
                    saving={saving}
                    onValidationChange={(valid) => onWizardStepValidationChange(1, valid)}
                    isReadOnly={false}
                    isNew={true}
                    category={category}
                    templateOptions={templateOptions}
                />
                <TemplateRolePermissions
                    template={template}
                    onChange={setTemplate}
                    loading={loading}
                    saving={saving}
                    onValidationChange={(valid) => onWizardStepValidationChange(2, valid)}
                    isReadOnly={true}
                    isNew={true}
                    userRoles={userRoles}
                />
            </Wizard>

            <AlertUnsavedData
                isDirty={isTemplateDirty && !createdTemplateId}>
            </AlertUnsavedData>
        </div>)}
        no={() => (
            <PageRestricted />
        )}
    />
}

NewTemplate.propTypes = {
  breadcrumbs: PropTypes.array,
  useGlobalConfig: PropTypes.bool
}; 